import * as React from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import type { ApiError } from "@eduviaos/shared";
import { SeatSlotsCard } from "@/components/seating/SeatSlotsCard";
import { useCanEditSeating } from "@/hooks/useSeatingAuthoring";
import {
  FIXED_METHOD_LABELS,
  FIXED_ORDER_LABELS,
  MIXED_DEFAULT_PLAN_LABELS,
  SEAT_MODE_HELP,
  SEAT_MODE_LABELS,
  buildWriteBody,
  useResetSeatConfig,
  useSeatConfig,
  useWriteSeatConfig,
  type FixedAssignmentMethod,
  type FixedAssignmentOrder,
  type MixedDefaultPlan,
  type SeatAssignmentConfigBody,
  type SeatAssignmentConfigResponse,
  type SeatAssignmentMode,
} from "@/lib/seatAssignmentConfig/api";

/**
 * Phase F-finale-7 Slice 7b.1 — IA tenant-self seat-assignment-config
 * editor.
 *
 * The study-centre counterpart to the admission-funnel editor (Slice 6b):
 * a typed editor for the `seat_assignment_config` capability over the
 * real-auth `/api/seating/config` surface (Slice 7b.0). Lets a
 * `manage_zones`-holding admin pick the seat mode, toggle the booking
 * policy flags, and — in fixed mode — choose the permanent-allocation
 * method + ordering. Reset reverts to the vertical default.
 *
 * Write controls are gated by `useCanEditSeating`; the editor is read-only
 * (with a view-only notice) when the principal lacks `manage_zones`. The
 * BE re-checks the same permission + the cross-field `mode === "fixed"`
 * invariant, so this is a UX layer, not the only line of defence.
 */

const SEAT_MODES: readonly SeatAssignmentMode[] = [
  "none",
  "zone_only",
  "dynamic",
  "fixed",
  "mixed",
];
const MIXED_DEFAULT_PLANS: readonly MixedDefaultPlan[] = [
  "first_come",
  "flexible_hours",
  "no_seat",
];
const FIXED_METHODS: readonly FixedAssignmentMethod[] = [
  "admin_assigns",
  "student_picks",
  "auto_round_robin",
];
const FIXED_ORDERS: readonly FixedAssignmentOrder[] = ["alphabetical", "merit", "manual"];

type PolicyFlagKey =
  | "require_booking"
  | "allow_seat_switch"
  | "allow_session_extension"
  | "enforce_zone_capacity"
  | "waitlist_enabled";

/** The booking-policy boolean flags, in render order. */
const FLAGS: ReadonlyArray<{
  key: PolicyFlagKey;
  label: string;
  description: string;
}> = [
  {
    key: "require_booking",
    label: "Require booking",
    description: "Members must hold a booking before they can occupy a seat.",
  },
  {
    key: "allow_seat_switch",
    label: "Allow seat switch",
    description: "Members may move to a different free seat mid-session.",
  },
  {
    key: "allow_session_extension",
    label: "Allow session extension",
    description: "Members can extend an active session if the seat is still free.",
  },
  {
    key: "enforce_zone_capacity",
    label: "Enforce zone capacity",
    description: "Block bookings once a zone reaches its configured seat count.",
  },
  {
    key: "waitlist_enabled",
    label: "Waitlist enabled",
    description: "Queue members for a seat when their preferred zone is full.",
  },
];

function pickBody(c: SeatAssignmentConfigResponse): SeatAssignmentConfigBody {
  return {
    mode: c.mode,
    require_booking: c.require_booking,
    allow_seat_switch: c.allow_seat_switch,
    allow_session_extension: c.allow_session_extension,
    fixed_assignment_method: c.fixed_assignment_method,
    fixed_assignment_order: c.fixed_assignment_order,
    enforce_zone_capacity: c.enforce_zone_capacity,
    waitlist_enabled: c.waitlist_enabled,
    custom_duration_enabled: c.custom_duration_enabled ?? false,
    custom_hourly_rate_paise: c.custom_hourly_rate_paise ?? 0,
    mixed_default_plan: c.mixed_default_plan ?? "first_come",
  };
}

function rupeeInputFromPaise(paise: number): string {
  if (!paise) return "";
  const rupees = paise / 100;
  return Number.isInteger(rupees) ? String(rupees) : rupees.toFixed(2);
}

function paiseFromRupeeInput(raw: string): number {
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

function sameBody(a: SeatAssignmentConfigBody, b: SeatAssignmentConfigBody): boolean {
  return (
    a.mode === b.mode &&
    a.require_booking === b.require_booking &&
    a.allow_seat_switch === b.allow_seat_switch &&
    a.allow_session_extension === b.allow_session_extension &&
    a.fixed_assignment_method === b.fixed_assignment_method &&
    a.fixed_assignment_order === b.fixed_assignment_order &&
    a.enforce_zone_capacity === b.enforce_zone_capacity &&
    a.waitlist_enabled === b.waitlist_enabled &&
    a.custom_duration_enabled === b.custom_duration_enabled &&
    a.custom_hourly_rate_paise === b.custom_hourly_rate_paise &&
    a.mixed_default_plan === b.mixed_default_plan
  );
}

export function SeatConfigPage() {
  const { canEditSeating, missingRbac } = useCanEditSeating();
  const configQuery = useSeatConfig();

  if (configQuery.isLoading) {
    return (
      <div className="text-muted-foreground inline-flex items-center gap-2 py-12 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading seat configuration…
      </div>
    );
  }

  if (configQuery.isError || !configQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Couldn&apos;t load your seat configuration</CardTitle>
          <CardDescription>
            Something went wrong fetching the seat-assignment settings. Try again in a moment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => void configQuery.refetch()} variant="outline" size="sm">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <SeatConfigEditorInner
      initial={configQuery.data}
      canEdit={canEditSeating}
      missingRbac={missingRbac}
    />
  );
}

interface SeatConfigEditorInnerProps {
  initial: SeatAssignmentConfigResponse;
  canEdit: boolean;
  missingRbac: boolean;
}

function SeatConfigEditorInner({ initial, canEdit, missingRbac }: SeatConfigEditorInnerProps) {
  const writeMutation = useWriteSeatConfig();
  const resetMutation = useResetSeatConfig();

  const serverBody = React.useMemo(() => pickBody(initial), [initial]);
  const [draft, setDraft] = React.useState<SeatAssignmentConfigBody>(serverBody);
  const [rateRupees, setRateRupees] = React.useState(() =>
    rupeeInputFromPaise(serverBody.custom_hourly_rate_paise),
  );

  // Re-seed the draft whenever the server copy changes (after a successful
  // save / reset, the mutation writes the fresh payload into the cache,
  // which flows back in as a new `initial` and clears the dirty state).
  const seededRef = React.useRef(initial);
  React.useEffect(() => {
    if (seededRef.current !== initial) {
      seededRef.current = initial;
      const next = pickBody(initial);
      setDraft(next);
      setRateRupees(rupeeInputFromPaise(next.custom_hourly_rate_paise));
    }
  }, [initial]);

  const readOnly = !canEdit;
  const dirty = !sameBody(draft, serverBody);
  const rateMissing = draft.custom_duration_enabled && draft.custom_hourly_rate_paise <= 0;
  const isFixed = draft.mode === "fixed";

  function setMode(mode: SeatAssignmentMode): void {
    setDraft((prev) => {
      if (mode === "fixed") {
        return {
          ...prev,
          mode,
          fixed_assignment_method: prev.fixed_assignment_method ?? "admin_assigns",
          fixed_assignment_order: prev.fixed_assignment_order ?? "alphabetical",
        };
      }
      return { ...prev, mode, fixed_assignment_method: null, fixed_assignment_order: null };
    });
  }

  function setFlag(key: PolicyFlagKey, value: boolean): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function onSave(): void {
    writeMutation.mutate(buildWriteBody(draft), {
      onSuccess: () => toast.success("Seat configuration saved"),
      onError: (err: ApiError) =>
        toast.error(err.message || "Couldn't save the seat configuration"),
    });
  }

  function onReset(): void {
    resetMutation.mutate(undefined, {
      onSuccess: () => toast.success("Back to the recommended setup"),
      onError: (err: ApiError) =>
        toast.error(err.message || "Couldn't reset the seat configuration"),
    });
  }

  const saving = writeMutation.isPending;
  const resetting = resetMutation.isPending;

  return (
    <div className="space-y-6 pb-24">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Seat assignment</PageHeaderHeading>
            <PageHeaderDescription>
              Choose how learners occupy seats — from a simple check-in to permanent allocated seats
              — and the booking policy that backs it.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={initial.source === "tenant" ? "default" : "outline"}>
          {initial.source === "tenant" ? "Customised" : "Recommended"}
        </Badge>
        {initial.source === "tenant" && !readOnly ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={onReset}
            disabled={resetting}
          >
            Reset to recommended setup
          </Button>
        ) : null}
      </div>

      {missingRbac ? (
        <Card data-testid="seating-missing-rbac-notice" className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">View-only access</CardTitle>
            <CardDescription>
              You don&apos;t have the <span className="font-medium">Manage zones</span> permission,
              so seat-assignment settings are read-only. Ask an administrator to grant it if you
              need to make changes.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Seat mode</CardTitle>
          <CardDescription>How members are matched to a place in the centre.</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={draft.mode}
            onValueChange={(v) => setMode(v as SeatAssignmentMode)}
            disabled={readOnly}
            className="grid gap-2"
          >
            {SEAT_MODES.map((m) => (
              <div
                key={m}
                className={cn(
                  "border-border/60 flex items-start gap-3 rounded-md border px-3 py-2.5",
                  draft.mode === m && "border-primary bg-primary/5",
                )}
              >
                <RadioGroupItem id={`seat-mode-${m}`} value={m} className="mt-0.5" />
                <Label htmlFor={`seat-mode-${m}`} className="grid cursor-pointer gap-0.5">
                  <span className="text-sm font-medium">{SEAT_MODE_LABELS[m]}</span>
                  <span className="text-muted-foreground text-xs font-normal">
                    {SEAT_MODE_HELP[m]}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {isFixed ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Permanent allocation</CardTitle>
            <CardDescription>
              Who allocates each member&apos;s permanent seat, and in what order.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="fixed-method">Assignment method</Label>
              <Select
                value={draft.fixed_assignment_method ?? "admin_assigns"}
                onValueChange={(v) =>
                  setDraft((prev) => ({
                    ...prev,
                    fixed_assignment_method: v as FixedAssignmentMethod,
                  }))
                }
                disabled={readOnly}
              >
                <SelectTrigger id="fixed-method" aria-label="Assignment method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIXED_METHODS.map((fm) => (
                    <SelectItem key={fm} value={fm}>
                      {FIXED_METHOD_LABELS[fm]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="fixed-order">Assignment order</Label>
              <Select
                value={draft.fixed_assignment_order ?? "alphabetical"}
                onValueChange={(v) =>
                  setDraft((prev) => ({
                    ...prev,
                    fixed_assignment_order: v as FixedAssignmentOrder,
                  }))
                }
                disabled={readOnly}
              >
                <SelectTrigger id="fixed-order" aria-label="Assignment order">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIXED_ORDERS.map((fo) => (
                    <SelectItem key={fo} value={fo}>
                      {FIXED_ORDER_LABELS[fo]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {draft.mode === "mixed" ? (
        <Card data-testid="seat-mixed-card">
          <CardHeader>
            <CardTitle className="text-base">Learner seat plans</CardTitle>
            <CardDescription>
              Give each learner their own plan from{" "}
              <Link to="/seating/plans" className="text-primary font-medium underline">
                Seat plans
              </Link>
              . Learners without a plan follow the default below.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-1.5 sm:max-w-sm">
            <Label htmlFor="mixed-default-plan">Default plan</Label>
            <Select
              value={draft.mixed_default_plan ?? "first_come"}
              onValueChange={(v) =>
                setDraft((prev) => ({ ...prev, mixed_default_plan: v as MixedDefaultPlan }))
              }
              disabled={readOnly}
            >
              <SelectTrigger id="mixed-default-plan" aria-label="Default plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MIXED_DEFAULT_PLANS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {MIXED_DEFAULT_PLAN_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Custom hours</CardTitle>
          <CardDescription>
            Let a learner book a stretch of whole hours as well as a named shift. The charge is the
            hours times the price you set here.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <PolicySwitch
            label="Allow custom hours"
            description="Named shifts stay available. Turn this off to take only the shifts below."
            checked={draft.custom_duration_enabled}
            disabled={readOnly}
            onCheckedChange={(v) => setDraft((prev) => ({ ...prev, custom_duration_enabled: v }))}
          />
          {draft.custom_duration_enabled ? (
            <div className="grid gap-1.5">
              <Label htmlFor="hourly-rate">Price per hour (₹)</Label>
              <Input
                id="hourly-rate"
                inputMode="decimal"
                value={rateRupees}
                disabled={readOnly}
                placeholder="200"
                onChange={(e) => {
                  const raw = e.target.value;
                  setRateRupees(raw);
                  setDraft((prev) => ({
                    ...prev,
                    custom_hourly_rate_paise: paiseFromRupeeInput(raw),
                  }));
                }}
              />
              {rateMissing ? (
                <p className="text-destructive text-xs">Enter a price above zero before saving.</p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  Two hours at this price is charged as twice the rate.
                </p>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Booking policy</CardTitle>
          <CardDescription>Rules applied to every seat occupation.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2">
          {FLAGS.map((f) => (
            <PolicySwitch
              key={f.key}
              label={f.label}
              description={f.description}
              checked={Boolean(draft[f.key])}
              disabled={readOnly}
              onCheckedChange={(v) => setFlag(f.key, v)}
            />
          ))}
        </CardContent>
      </Card>

      <SeatSlotsCard canEdit={!readOnly} />

      {dirty && !readOnly ? (
        <div className="border-border/60 bg-card/95 sticky bottom-3 z-10 flex items-center justify-between gap-3 rounded-md border px-4 py-3 shadow-sm backdrop-blur">
          <span className="text-muted-foreground text-sm">You have unsaved changes.</span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft(serverBody);
                setRateRupees(rupeeInputFromPaise(serverBody.custom_hourly_rate_paise));
              }}
              disabled={saving}
            >
              Discard
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving || rateMissing}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface PolicySwitchProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (value: boolean) => void;
}

function PolicySwitch({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: PolicySwitchProps) {
  return (
    <label className="border-border/60 bg-muted/20 flex items-start gap-3 rounded-md border px-3 py-2.5">
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
      <div className="grid gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-muted-foreground text-xs">{description}</span>
      </div>
    </label>
  );
}
